"use client";
import React, { useState, useTransition, useEffect } from "react";
import { useSignIn, useAuth } from "@clerk/nextjs";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Eye, EyeOff } from "lucide-react";

export default function SignInPage() {
  const router = useRouter();
  const { isLoaded, signIn, setActive } = useSignIn();
  const { userId, isLoaded: authLoaded } = useAuth();
  const [isPending, startTransition] = useTransition();
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if user is already signed in (honor redirect_url e.g. from admin, else dashboard)
  useEffect(() => {
    if (authLoaded && userId) {
      let target = "/dashboard";
      if (typeof window !== "undefined") {
        const redirectUrl = new URLSearchParams(window.location.search).get("redirect_url");
        if (redirectUrl) {
          try {
            const url = new URL(redirectUrl);
            if (url.origin === window.location.origin && url.pathname.startsWith("/")) {
              target = url.pathname + url.search;
            }
          } catch {
            /* invalid URL, use default */
          }
        }
      }
      router.push(target);
    }
  }, [authLoaded, userId, router]);

  const [formData, setFormData] = useState({
    identifier: '', // email or username
    password: '',
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!isLoaded) {
      toast.error('Authentication system is not ready. Please try again.');
      return;
    }

    startTransition(async () => {
      try {
        // Trim whitespace from both fields and remove any invisible characters
        let trimmedIdentifier = formData.identifier.trim().replace(/\s+/g, '');
        const trimmedPassword = formData.password.trim();
        
        if (!trimmedIdentifier || !trimmedPassword) {
          toast.error('Please enter both email/username and password.');
          return;
        }

        // Remove any non-printable characters that might cause issues
        trimmedIdentifier = trimmedIdentifier.replace(/[\x00-\x1F\x7F]/g, '');

        // Lowercase only if it's an email (contains @), preserve case for usernames
        if (trimmedIdentifier.includes('@')) {
          trimmedIdentifier = trimmedIdentifier.toLowerCase();
        }

        // Create sign-in attempt
        const result = await signIn.create({
          identifier: trimmedIdentifier,
          password: trimmedPassword,
        });

        // Complete the sign-in
        if (result.status === 'complete') {
          await setActive({ session: result.createdSessionId });
          toast.success('Sign in successful!');
          
          setTimeout(() => {
            window.location.reload();
          }, 10000);
        } else {
          toast.error('Sign in incomplete. Please try again.');
        }
      } catch (err: any) {
        const errorMessage = err.errors?.[0]?.message || 'Failed to sign in. Please check your credentials.';
        toast.error(errorMessage);
      }
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Don't trim on input - only trim on submit to avoid cursor position issues
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Show loading state while checking authentication
  if (!authLoaded) {
    return (
      <main className="grid place-items-center pt-4">
        <div className="text-center">Loading...</div>
      </main>
    );
  }

  // Don't render the form if user is already signed in (will redirect)
  if (userId) {
    return null;
  }

  return (
    <main className="grid place-items-center pt-4">
      <div className="max-w-md w-full mx-auto rounded-none md:rounded-2xl p-4 md:p-8 shadow-input bg-white min-h-screen dark:bg-black">
        <div className="flex justify-center mb-4">
          <Image src="/bf.jpeg" className='rounded-full' alt="Logo" width={100} height={100} />
        </div>
        <h2 className="font-bold text-xl text-neutral-800 dark:text-neutral-200 text-center">
          Sign In
        </h2>
        <p className="text-neutral-600 text-sm max-w-sm mt-2 dark:text-neutral-300 text-center">
          Welcome back! Sign in to your Bitcoin Fortress account.
        </p>

        <form className="my-8" onSubmit={handleSubmit}>
          <LabelInputContainer className="mb-4">
            <Label htmlFor="identifier">Email or Username</Label>
            <Input
              id="identifier"
              name="identifier"
              placeholder="john@example.com or johndoe"
              type="text"
              autoComplete="username"
              value={formData.identifier}
              onChange={handleInputChange}
              required
              disabled={isPending || !isLoaded}
            />
          </LabelInputContainer>

          <LabelInputContainer className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="password">Password</Label>
              <Link
                href="/forgot-password"
                className="text-sm text-orange-600 dark:text-orange-500 hover:text-orange-800 dark:hover:text-orange-600 font-medium transition-colors"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password"
                name="password"
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={isPending || !isLoaded}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={isPending || !isLoaded}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </LabelInputContainer>

          <button
            className="bg-gradient-to-br relative group/btn from-black dark:from-zinc-900 dark:to-zinc-900 to-neutral-600 block dark:bg-zinc-800 w-full text-white rounded-md h-10 font-medium shadow-[0px_1px_0px_0px_#ffffff40_inset,0px_-1px_0px_0px_#ffffff40_inset] dark:shadow-[0px_1px_0px_0px_var(--zinc-800)_inset,0px_-1px_0px_0px_var(--zinc-800)_inset]"
            type="submit"
            disabled={isPending || !isLoaded}
          >
            {isPending ? 'Signing in...' : 'Sign In'}
            <BottomGradient />
          </button>
        </form>

        <div className="bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent my-8 h-[1px] w-full" />
        
        <div className="text-center">
          <p className="text-neutral-600 text-sm dark:text-neutral-400">
            Don't have an account?{" "}
            <Link 
              href="/sign-up" 
              className="text-orange-600 dark:text-orange-500 hover:text-orange-800 dark:hover:text-orange-600 font-medium transition-colors"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}

const BottomGradient = () => {
  return (
    <>
      <span className="group-hover/btn:opacity-100 block transition duration-500 opacity-0 absolute h-px w-full -bottom-px inset-x-0 bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
      <span className="group-hover/btn:opacity-100 blur-sm block transition duration-500 opacity-0 absolute h-px w-1/2 mx-auto -bottom-px inset-x-10 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />
    </>
  );
};

const LabelInputContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn("flex flex-col space-y-2 w-full", className)}>
      {children}
    </div>
  );
};
